import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Breadcrumb, Button, Checkbox, Form, Popconfirm, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@src/utils/constants';
import AdminOnly from '@src/components/common/AdminOnly';
import {
  clearGlobalMute,
  fetchGlobalMute,
  GlobalMuteRow,
  upsertGlobalMute,
} from '@src/api/globalMute';
import GlobalMuteWindowPicker from './GlobalMuteWindowPicker';
import { getGlobalMuteProductLabel } from './productLabels';
import {
  BodyWrapper,
  Description,
  Divider,
  GlobalMuteHeaderWrapper,
  LeftSideHeader,
  RightSideHeader,
  Title,
} from './styled';

const GLOBAL_MUTE_QUERY_KEY = 'global-mute';

// "Video" / "Video and Ad"
const joinLabels = (labels: string[]): string => {
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

// Products mutable from the current place. Video and Ad share one place; App
// stays on its own. Each product maps to the `mode` the server uses to derive
// its context (ei -> video, ai -> ad, app -> eco).
const PRODUCTS_FOR_MODULE: { mode: string; label: string }[] = isAppModule
  ? [{ mode: 'app', label: getGlobalMuteProductLabel('app') }]
  : [
      { mode: 'ei', label: getGlobalMuteProductLabel('ei') },
      { mode: 'ai', label: getGlobalMuteProductLabel('ai') },
    ];

const GlobalMuteDrawerContent = ({ closeDrawer }: { closeDrawer: () => void }) => {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const currentMode = (CURRENT_PRODUCT_MODE || 'EI').toLowerCase();

  // One status query per product. A product the customer does not have makes the
  // server reject with 400 (LoadProduct validation), which we treat as
  // "unavailable" and drop from the options.
  const statusQueries = useQueries({
    queries: PRODUCTS_FOR_MODULE.map((product) => ({
      queryKey: [GLOBAL_MUTE_QUERY_KEY, product.mode],
      queryFn: async () => {
        const res = await fetchGlobalMute(product.mode);
        return (res?.data?.data ?? null) as GlobalMuteRow | null;
      },
      retry: false,
    })),
  });

  const isLoading = statusQueries.some((q) => q.isLoading);

  const products = useMemo(
    () =>
      PRODUCTS_FOR_MODULE.map((product, index) => ({
        ...product,
        mute: (statusQueries[index]?.data ?? null) as GlobalMuteRow | null,
        unavailable: statusQueries[index]?.isError ?? false,
      })).filter((product) => !product.unavailable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isLoading,
      ...statusQueries.map((q) => q.dataUpdatedAt),
      ...statusQueries.map((q) => q.errorUpdatedAt),
    ],
  );

  const [selected, setSelected] = useState<string[] | null>(null);
  const seededRef = useRef(false);

  // Seed the selection (products currently muted, else the current page product)
  // and the shared window once statuses have settled.
  useEffect(() => {
    if (isLoading || seededRef.current || !products.length) return;
    seededRef.current = true;

    const mutedModes = products.filter((p) => p.mute).map((p) => p.mode);
    if (mutedModes.length) {
      setSelected(mutedModes);
    } else {
      const fallback = products.find((p) => p.mode === currentMode)?.mode || products[0].mode;
      setSelected([fallback]);
    }

    const seedRow =
      products.find((p) => p.mode === currentMode)?.mute || products.find((p) => p.mute)?.mute;
    form.setFieldsValue({
      muteFrom: seedRow ? moment(seedRow.muteFrom) : moment(),
      muteUntil: seedRow ? moment(seedRow.muteUntil) : moment().add(4, 'hour'),
    });
  }, [isLoading, products, currentMode, form]);

  const hasSelectedMuted = products.some((p) => p.mute && selected?.includes(p.mode));

  const invalidate = () => queryClient.invalidateQueries([GLOBAL_MUTE_QUERY_KEY]);

  const { mutate: submitSave, isLoading: isSaving } = useMutation(
    async (values: { muteFrom: moment.Moment; muteUntil: moment.Moment }) => {
      const window = { muteFrom: values.muteFrom.format(), muteUntil: values.muteUntil.format() };
      const targets = products.filter((product) => selected?.includes(product.mode));
      await Promise.all(targets.map((product) => upsertGlobalMute(window, product.mode)));
      return targets.map((product) => product.label);
    },
    {
      onSuccess: (labels) => {
        invalidate();
        message.success(`Global Mute is now ON for ${joinLabels(labels)}.`);
      },
      onError: () => {
        message.error('Failed to save global mute. Please try again.');
      },
    },
  );

  const { mutate: submitClearAll, isLoading: isClearing } = useMutation(
    async () => {
      const targets = products.filter((p) => p.mute && selected?.includes(p.mode));
      await Promise.all(targets.map((product) => clearGlobalMute(product.mode)));
      return targets.map((product) => product.label);
    },
    {
      onSuccess: (labels) => {
        invalidate();
        message.success(`Global Mute is now OFF for ${joinLabels(labels)}.`);
      },
      onError: () => {
        message.error('Failed to clear global mute. Please try again.');
      },
    },
  );

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => submitSave(values as any))
      .catch(() => undefined);
  };

  // Simple ON/OFF status header (ON = currently suppressing). One line per
  // product so each shows its own state and mute window (Video and Ad can differ).
  // ON is red, OFF is black; a subtle "until ..." is appended when ON.
  const renderStatus = () => {
    const line = (label: string, on: boolean, until: moment.Moment | null) => (
      <div key={label} style={{ marginBottom: 4, fontWeight: 500 }}>
        {showProductSelector ? `${label} \u2014 ` : ''}
        Global Mute:{' '}
        <span style={{ color: on ? '#cf1322' : '#000', fontWeight: 700 }}>{on ? 'ON' : 'OFF'}</span>
        {on && until ? (
          <span style={{ color: '#999', fontWeight: 400, marginLeft: 6 }}>
            until {until.format('MMM D, YYYY h:mm A')}
          </span>
        ) : null}
      </div>
    );

    return (
      <div style={{ marginBottom: 16 }}>
        {products.map((product) => {
          const on = !!product.mute?.isActive;
          const until = on && product.mute ? moment(product.mute.muteUntil) : null;
          return line(product.label, on, until);
        })}
      </div>
    );
  };

  const showProductSelector = products.length > 1;

  // Mirror the window picker's End-time rules so the Mute ON button reflects
  // validity without waiting for a submit attempt: end must exist, be after
  // start, and keep the window within the 24-hour cap.
  const muteFrom = Form.useWatch('muteFrom', form) as moment.Moment | undefined;
  const muteUntil = Form.useWatch('muteUntil', form) as moment.Moment | undefined;
  const endTimeInvalid =
    !muteFrom ||
    !muteUntil ||
    !muteUntil.isAfter(muteFrom) ||
    muteUntil.diff(muteFrom, 'hours', true) > 24;

  const saveDisabled = !selected || selected.length === 0 || endTimeInvalid;
  const headerLabel = getGlobalMuteProductLabel(currentMode);

  return (
    <>
      <GlobalMuteHeaderWrapper>
        <LeftSideHeader>
          <Title>{headerLabel}</Title>
          <Divider />
          <Breadcrumb separator=">">
            <Breadcrumb.Item>
              <Title>
                Global Mute
                <AdminOnly style={{ position: 'relative', top: '-3px', marginLeft: '10px' }} />
              </Title>
            </Breadcrumb.Item>
          </Breadcrumb>
        </LeftSideHeader>
        <RightSideHeader>
          <CloseOutlined style={{ paddingTop: '8px' }} onClick={closeDrawer} />
        </RightSideHeader>
      </GlobalMuteHeaderWrapper>

      <BodyWrapper>
        {isLoading ? (
          <Spin />
        ) : (
          <>
            {renderStatus()}

            <Description>
              Global Mute, while <strong>ON</strong>, suppresses{' '}
              <strong>all notifications across every channel</strong> for{' '}
              {isAppModule ? 'AI alerts' : 'both AI and manual alerts'} of the selected products.
              {isAppModule ? ' ' : ' Auto-insights are not affected. '}
              The window is capped at 24 hours.
            </Description>

            <Form form={form} layout="vertical" requiredMark={false} disabled={!isAdminUser}>
              {showProductSelector ? (
                <Form.Item label="Products">
                  <Checkbox.Group
                    options={products.map((p) => ({ label: p.label, value: p.mode }))}
                    value={selected ?? []}
                    onChange={(vals) => setSelected(vals as string[])}
                  />
                </Form.Item>
              ) : null}
              <GlobalMuteWindowPicker form={form} />
            </Form>

            {isAdminUser ? (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  danger
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={saveDisabled}
                  style={{ marginRight: 12 }}
                >
                  Mute ON
                </Button>
                {hasSelectedMuted ? (
                  <Popconfirm
                    title="Turn Global Mute OFF for the selected products?"
                    okText="Mute OFF"
                    cancelText="Cancel"
                    onConfirm={() => submitClearAll()}
                  >
                    <Button loading={isClearing} style={{ color: '#000', borderColor: '#000' }}>
                      Mute OFF
                    </Button>
                  </Popconfirm>
                ) : null}
              </div>
            ) : (
              <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                message="Only Admins have access to edit."
              />
            )}
          </>
        )}
      </BodyWrapper>
    </>
  );
};

export default GlobalMuteDrawerContent;
